<script setup>
import { ref, defineProps } from "vue";
import JourneyDepthItem from "./JourneyDepthItem.vue";
import fetchData from "@/utils/fetchData";
import { cents } from "@/utils/format";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
});

const journey = ref({
  prompt: "",
  questions: [],
  questionCost: 0,
  totalCost: 0,
  generations: [],
});

const loadJourney = async () => {
  const result = await fetchData("getIndividualJourney", {
    journeyId: props.id,
  });
  journey.value = result.journey;
  formatQuestions();
  formatGenerations();
};

const formatQuestions = () => {
  let questionsFormatted = "";
  try {
    const questionJSON = JSON.parse(journey.value.questions);
    questionJSON.forEach((question) => {
      questionsFormatted += `Question: ${question.question}\n`;
      question.suggestedAnswers.forEach((answer) => {
        questionsFormatted += `\t${answer}\n`;
      });
      questionsFormatted += "\n";
    });
    questionsFormatted = questionsFormatted.slice(0, -1);
    journey.value.questions = questionsFormatted;
  } catch {
    journey.value.questions =
      "Error parsing questions:\n\n" + journey.value.questions;
  }
};

let personalInfo = "";
const formatGenerations = () => {
  journey.value.generations.forEach((generation) => {
    if (personalInfo === "" && generation.inputs[0].personalInfo) {
      personalInfo = formatPersonalInfo(generation.inputs[0].personalInfo);
    }
    generation.answers = formatAnswers(generation.inputs[0]);
    generation.style = formatStyle(generation.inputs[0]);
    generation.context = formatContext(generation.inputs[0]);
  });
};

const formatPersonalInfo = (personalInfo) => {
  let hasPersonalInfo = false;
  let personalInfoFormatted = "";
  if (personalInfo.name) {
    personalInfoFormatted += `Name: ${personalInfo.name}\n`;
    hasPersonalInfo = true;
  }
  if (personalInfo.gender) {
    personalInfoFormatted += `Gender: ${personalInfo.gender}\n`;
    hasPersonalInfo = true;
  }
  if (personalInfo.email) {
    personalInfoFormatted += `Email: ${personalInfo.email}\n`;
    hasPersonalInfo = true;
  }
  if (personalInfo.job) {
    personalInfoFormatted += `Job Title: ${personalInfo.job}\n`;
    hasPersonalInfo = true;
  }
  if (personalInfo.country) {
    personalInfoFormatted += `Country of Residence: ${personalInfo.country}\n`;
    hasPersonalInfo = true;
  }
  const profile = [
    ["company", "Company"],
    ["address", "Address"],
    ["phone", "Phone"],
    ["website", "Website"],
    ["signOff", "Sign-off"],
  ];
  profile.forEach(([key, label]) => {
    if (personalInfo[key]) {
      personalInfoFormatted += `${label}: ${personalInfo[key]}\n`;
      hasPersonalInfo = true;
    }
  });
  if (personalInfo.categories?.length > 0) {
    hasPersonalInfo = true;
    personalInfo.categories.forEach((category) => {
      personalInfoFormatted += `${category.name}: ${category.answer}\n`;
    });
  }
  if (personalInfo.extraInfo) {
    personalInfoFormatted += `Extra Info:\n${personalInfo.extraInfo}\n`;
    hasPersonalInfo = true;
  }
  if (!hasPersonalInfo) {
    return "";
  }
  return personalInfoFormatted.slice(0, -1);
};

const formatAnswers = (inputs) => {
  if (!inputs.questions) {
    return "";
  }
  const questions = inputs.questions;
  if (questions.length === 0) {
    return "";
  }
  let answersFormatted = "";
  questions.forEach((question) => {
    answersFormatted += `Question: ${question.question}\n`;
    (question.answers || [question.answer].filter(Boolean)).forEach(
      (answer) => {
        answersFormatted += `\t\t${answer}\n`;
      }
    );
    answersFormatted += "\n";
  });
  return answersFormatted.slice(0, -1);
};

const formatStyle = (inputs) => {
  let hasSelectedSomeStyle = false;
  let styleFormatted = "";
  if (inputs.tone) {
    styleFormatted += `Tone: ${inputs.tone}\n`;
    hasSelectedSomeStyle = true;
  } else {
    styleFormatted += "Tone: Unspecified\n";
  }
  if (inputs.length) {
    styleFormatted += `Length: ${inputs.length}\n`;
    hasSelectedSomeStyle = true;
  } else {
    styleFormatted += "Length: Unspecified\n";
  }
  if (inputs.format) {
    styleFormatted += `Format: ${inputs.format}\n`;
    hasSelectedSomeStyle = true;
  } else {
    styleFormatted += "Format: Unspecified\n";
  }
  if (inputs.language) {
    styleFormatted += `Language: ${inputs.language}\n`;
    hasSelectedSomeStyle = true;
  } else {
    styleFormatted += "Language: Unspecified\n";
  }
  if (inputs.output) {
    styleFormatted += `Output: ${inputs.output}\n`;
    hasSelectedSomeStyle = true;
  }
  if (inputs.research) {
    styleFormatted += "Research: on\n";
    hasSelectedSomeStyle = true;
  }
  if (!hasSelectedSomeStyle) {
    return "";
  }
  return styleFormatted.slice(0, -1);
};

const formatContext = (inputs) => {
  const lines = [];
  if (inputs.context) {
    lines.push(`Context: ${inputs.context}`);
  }
  (inputs.documents || []).forEach((document) => {
    lines.push(
      `Document: ${document.name} (${document.characters} characters${
        document.truncated ? ", shortened" : ""
      })`
    );
  });
  (inputs.images || []).forEach((image) => {
    lines.push(`Photo: ${image.name || "unnamed"}`);
  });
  return lines.join("\n");
};

loadJourney();
</script>

<template>
  <section class="rounded-xl bg-canvas px-5 py-5 dark:bg-night">
    <p class="card-note mb-5">
      Whole journey cost, including edits and pictures:
      <strong class="text-primary dark:text-white">{{
        cents(journey.totalCost)
      }}</strong>
    </p>
    <JourneyDepthItem
      title="Prompt"
      index="1"
      :text="`${journey.prompt}`"
      :cols="false"
    />
    <JourneyDepthItem
      title="Questions"
      index="2"
      :cost="cents(journey.questionCost)"
      :text="journey.questions"
    />
    <JourneyDepthItem
      v-if="personalInfo.length > 0"
      title="Personal Info"
      :index="3"
      :text="personalInfo"
    />
    <div v-for="(generation, index) in journey.generations" :key="generation">
      <JourneyDepthItem
        v-if="generation.context.length > 0"
        title="Context"
        :index="4 + index * 4"
        :text="generation.context"
      />
      <JourneyDepthItem
        v-if="generation.answers.length > 0"
        title="Answers"
        :index="5 + index * 4"
        :text="generation.answers"
      />
      <JourneyDepthItem
        v-if="generation.style.length > 0"
        title="Style"
        :index="6 + index * 4"
        :text="generation.style"
      />
      <JourneyDepthItem
        title="Generation"
        :index="7 + index * 4"
        :cost="cents(generation.generationCost)"
        :rating="generation.vote"
        :text="generation.result[0]"
      />
    </div>
  </section>
</template>
